import { Component, OnInit, Input, ElementRef, SimpleChanges, OnChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ToastPopupService } from 'src/app/shared/services/toast-popup.service';
import { UtilityService } from 'src/app/shared/services/utility.service';

/*
  For the toastClick function to popup a built in component we need to make sure the message
  that is passed in is formatted with the proper data-function attribute to ensure we can parse
  the funciton that we are trying to call. If we are calling a component we havent called before we need
  to add a reference to that component in the ToastPopupService and make that service call the component.

  For the first example of this in action checkout the login-page.component and look for the
  reference to toastPopupOptions.openSupportDialog.

*/

@Component({
  selector: 'app-toast',
  template: `<div
    class="alert"
    (click)="toastClick($event)"
    (keyup.Enter)="toastClick($event)"
    *ngIf="message"
    [ngClass]="determineToastType()"
    role="alert"
    aria-live="assertive"
  >
    <span aria-hidden="false" [innerHTML]="htmlData"></span>
    <!--<span class="sr-only" style="display:none" [innerHTML]="htmlDataSronly"></span>-->
  </div>`,
  styles: [
    `
      .alert a:hover {
        text-decoration: none;
        text-shadow: 1px 1px 1px #555;
      }

      .alert a {
        color: #fff !important;
        font-weight: 700;
        text-decoration: underline;
        cursor: pointer;
      }

      .toast-warning {
        color: var(--sed-engaged-black-color) !important;
        background-color: var(--sed-status-yellow-color) !important;
      }

      .toast-success {
        color: var(--sed-warm-white-color) !important;
        background-color: var(--sed-status-green-color) !important;
      }

      .toast-info {
        color: var(--sed-engaged-black-color);
        background-color: var(--sed-purple-40-color);
      }

      .toast-error {
        color: var(--sed-warm-white-color) !important;
        background-color: var(--sed-status-red-color) !important;
      }
    `,
  ],
})
export class ToastComponent implements OnInit, OnChanges {
  @Input() message: string;
  @Input() type: string;
  @Input() isToastFocus = 'false';

  public ToastEnum = ToastOptions;
  toastType: string;
  htmlData: any;
  htmlDataSronly: any;
  dataFunc: string;

  constructor(
    private sanitizer: DomSanitizer,
    private toastPopupService: ToastPopupService,
    private utilityService: UtilityService,
    private el: ElementRef
  ) {}

  ngOnInit() {
    this.toastType = this.determineToastType();
    this.htmlData = this.sanitizer.bypassSecurityTrustHtml(this.message);
    // this.htmlDataSronly = this.sanitizer.bypassSecurityTrustHtml(
    //   this.message.replace('-', ' ').replace("tabindex='0'", '')
    // );

    setTimeout(() => {
      if (this.isToastFocus == 'true') {
        const invalidControl = this.el.nativeElement.querySelector(
          '.' + this.toastType
        );
        if (invalidControl) {
          invalidControl.focus();
        }
      }
    }, 200);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['message']) {
      this.updateHtmlData();
    }
    
    if (changes['type']) {
      this.toastType = this.determineToastType();
    }
  }

  private updateHtmlData(): void {
    if (this.message) {
      this.htmlData = this.sanitizer.bypassSecurityTrustHtml(this.message);
    } else {
      this.htmlData = null;
    }
  }

  toastClick($event: any): void {
    this.dataFunc = $event.target.dataset.function;
    if (!this.utilityService.isNullOrEmpty(this.dataFunc))
      this.toastPopupService.handleToastPopup(this.dataFunc);
  }

  determineToastType(): string {
    let toastType;
    switch (this.type) {
      case this.ToastEnum.Success:
        toastType = 'toast-success';
        break;
      case this.ToastEnum.Error:
        toastType = 'toast-error';
        break;
      case this.ToastEnum.Info:
        toastType = 'toast-info';
        break;
      case this.ToastEnum.UserError:
        toastType = 'toast-error';
        break;
      default:
        toastType = 'alert-warning';
        break;
    }

    return toastType;
  }
}

export enum ToastOptions {
  Success = 'Success',
  Error = 'Error',
  Info = 'Info',
  NoBackground = 'NoBackground',
  UserError = 'UserError',
}
